import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Play } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { getBlogPosts } from '../lib/api';
import { sized } from '../lib/img';

function VideoBadge({ small }) {
  return (
    <span
      data-testid="video-badge"
      className={`absolute ${small ? 'top-3 left-3 w-9 h-9' : 'top-4 left-4 w-12 h-12'} rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white shadow-lg`}
    >
      <Play size={small ? 16 : 22} className="ml-0.5" fill="currentColor" />
    </span>
  );
}

const COVER_FALLBACK = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c';

export default function Blog() {
  const ref = useReveal();
  const [posts, setPosts] = useState([]);
  useEffect(() => { getBlogPosts().then(setPosts); }, []);

  const [featured, ...rest] = posts;
  const isVid = (p) => p?.type === 'video';

  return (
    <div ref={ref} className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
      <section className="reveal in max-w-2xl">
        <span className="eyebrow text-[#2E6BFF]">Resources</span>
        <h1 className="font-display font-extrabold text-[#0E1726] text-[40px] sm:text-[52px] tracking-tight leading-[1.02] mt-4">
          Field notes for the next chapter.
        </h1>
        <p className="text-[#5B6472] text-[17px] leading-relaxed mt-5">
          Practical essays, playbooks and short videos on product craft, AI, career reinvention, and leading with judgment.
        </p>
      </section>

      {featured && (
        <Link data-testid="blog-featured" to={`/blog/${featured.slug}`} className="reveal in block mt-10 bg-white rounded-[24px] overflow-hidden shadow-[0_25px_70px_-48px_rgba(14,23,38,0.4)] grid md:grid-cols-2 hover:-translate-y-0.5 transition-transform">
          <div className="relative h-64 md:h-auto">
            <img src={sized(featured.image || COVER_FALLBACK, 900)} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            {isVid(featured) && <VideoBadge />}
          </div>
          <div className="p-8 sm:p-10 flex flex-col justify-center">
            <span className="text-xs font-semibold text-[#2E6BFF]">{featured.tag}</span>
            <h2 className="font-display font-extrabold text-[#0E1726] text-[26px] sm:text-[30px] tracking-tight mt-3">{featured.title}</h2>
            <p className="text-[#5B6472] leading-relaxed mt-3">{featured.excerpt}</p>
            <div className="flex items-center gap-4 mt-5 text-sm text-[#5B6472]">
              <span className="flex items-center gap-1.5"><Clock size={14} /> {featured.read_time}</span>
              <span className="flex items-center gap-1.5 text-[#2E6BFF] font-semibold">{isVid(featured) ? 'Watch video' : 'Read article'} <ArrowRight size={15} /></span>
            </div>
          </div>
        </Link>
      )}

      <section className="grid md:grid-cols-3 gap-6 mt-8">
        {rest.map((p) => (
          <Link key={p.slug} data-testid={`blog-card-${p.slug}`} to={`/blog/${p.slug}`} className="reveal in bg-white rounded-2xl overflow-hidden shadow-[0_20px_50px_-42px_rgba(14,23,38,0.4)] hover:-translate-y-1 transition-transform">
            <div className="relative h-44">
              <img src={sized(p.image || COVER_FALLBACK, 600)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              {isVid(p) && <VideoBadge small />}
            </div>
            <div className="p-6">
              <span className="text-xs font-semibold text-[#2E6BFF]">{p.tag}</span>
              <h3 className="font-display font-bold text-[#0E1726] text-[19px] mt-2 leading-snug">{p.title}</h3>
              <p className="text-[#5B6472] text-sm leading-relaxed mt-2 line-clamp-2">{p.excerpt}</p>
              <span className="flex items-center gap-1.5 text-xs text-[#5B6472] mt-4">
                {isVid(p) ? <Play size={13} /> : <Clock size={13} />} {p.read_time}
              </span>
            </div>
          </Link>
        ))}
      </section>

      {posts.length === 0 && (
        <div className="text-center text-[#5B6472] py-20">No posts yet. Check back soon.</div>
      )}
    </div>
  );
}
